import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import dataCache from '../Utils/dataCache.js';
import JSZip from 'jszip';
import { useLoadingTracker } from '../contexts/LoadingTrackerContext';
import { fetchBlockModelSidecar, mergeBlockModelMeta } from '../Utils/blockModelSidecar.js';
import { formatBytes } from '../Utils/fetchWithProgress';
import { getModelArrayBuffer, matchCachedModel } from '../Utils/offlineCache';
import { getGradeColorFromThresholds, getNumericRampColor, isGradeBinVisible } from '../Utils/gradePalette.js';
import { getLithColorHex } from '../Utils/lithPalette.js';
import { OBJECT_CLIP_SHADER_HEADER, objectClipUniforms } from '../Utils/objectClip.js';
import { useClickStore } from '../store/clickStore.js';
import { getOriginERL } from '../config/siteConfig';
import { useGradePaletteStore } from '../store/gradePaletteStore.js';
import { useBlockModelDataStore } from '../store/blockModelDataStore';
import {
  useBlockModelFilterStore,
  resolvedGradeMin,
  DEFAULT_BLOCK_MODEL_MIN,
} from '../store/blockModelFilterStore.js';

const BLOCK_GAP = 0.9;
const COORD_ROUND = 100; // 1 cm

const SIZE_COLS = {
  x: ['XMSIZE', 'XSIZE', 'SIZE_X', 'DX', 'XINC', 'xinc','XINC', 'xinc', 'size (X)', 'size(X)', 'Size (X)', 'Size(X)', 'SIZE (X)', 'SIZE(X)', 'Size_X', 'size_x'],
  y: ['YMSIZE', 'YSIZE', 'SIZE_Y', 'DY', 'YINC', 'yinc','YINC', 'yinc', 'size (Y)', 'size(Y)', 'Size (Y)', 'Size(Y)', 'SIZE (Y)', 'SIZE(Y)', 'Size_Y', 'size_y'],
  z: ['ZMSIZE', 'ZSIZE', 'SIZE_Z', 'DZ', 'ZINC', 'zinc','ZINC', 'zinc', 'size (Z)', 'size(Z)', 'Size (Z)', 'Size(Z)', 'SIZE (Z)', 'SIZE(Z)', 'Size_Z', 'size_z'],
};

const GRADE_COLS = [
  'Au_gpt',
  'AU_GPT',
  'AU_GT',
  'AUGT',
  'AU_G_T',
  'AU_OK',
  'OKAU',
  'IDAU',
  'NS_AU',
  'AU',
  'Au',
  'GRADE',
  'AU_PPM',
  'AUPPM',
];

const COORD_NAMES = ['XC', 'XCENTRE', 'EAST', 'Easting', 'X', 'YC', 'YCENTRE', 'NORTH', 'Northing', 'Y', 'ZC', 'ZCENTRE', 'RL', 'ELEV', 'Z'];
const SIZE_NAMES = [...SIZE_COLS.x, ...SIZE_COLS.y, ...SIZE_COLS.z];
const SKIP_GRADE_NAMES = [
  ...COORD_NAMES,
  ...SIZE_NAMES,
  'XMORIG',
  'YMORIG',
  'ZMORIG',
  'XMIN',
  'YMIN',
  'ZMIN',
  'XMAX',
  'YMAX',
  'ZMAX',
  'NX',
  'NY',
  'NZ',
  'IJK',
  'INDEX',
  'DOMAIN',
  'ROCK',
  'DENSITY',
  'DENS',
  'SG',
  'TONNES',
  'TONS',
  'VOLUME',
  'ANGLE',
  'AZIMUTH',
  'DIP',
  'BEARING',
];

function datasetLength(ds) {
  if (!ds) return 0;
  if (Number.isInteger(ds.n) && ds.data) return ds.n;
  return ds.length || 0;
}

function getCol(ds, names) {
  if (!ds) return null;
  if (ds.data) {
    const cols = ds.columns || Object.keys(ds.data);
    const lower = new Map(cols.map((c) => [String(c).toLowerCase(), c]));
    for (let i = 0; i < names.length; i += 1) {
      const name = names[i];
      if (ds.data[name] !== undefined) return name;
      const hit = lower.get(String(name).toLowerCase());
      if (hit) return hit;
    }
    return null;
  }
  return pickColumn(ds[0], names);
}

function valueAt(ds, col, i) {
  if (!ds || !col) return undefined;
  if (ds.data) return ds.data[col][i];
  return ds[i][col];
}

function toColumnar(rows) {
  if (!rows) return null;
  if (Number.isInteger(rows.n) && rows.data) return rows;
  const columns = (rows.columns || Object.keys(rows[0] || {})).filter((c) => c != null && String(c).trim() !== '');
  const n = rows.length;
  const data = {};
  for (let c = 0; c < columns.length; c += 1) {
    const col = columns[c];
    let numeric = 0;
    const raw = new Array(n);
    for (let i = 0; i < n; i += 1) {
      const v = rows[i][col];
      raw[i] = v;
      if (v !== '' && v != null && Number.isFinite(num(v))) numeric += 1;
    }
    if (n > 0 && numeric / n >= 0.8) {
      const f = new Float32Array(n);
      for (let i = 0; i < n; i += 1) {
        const v = num(raw[i]);
        f[i] = Number.isFinite(v) ? v : Number.NaN;
      }
      data[col] = f;
    } else {
      data[col] = raw;
    }
  }
  return { columns, n, data };
}

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

function pickColumn(row, names) {
  if (!row) return null;
  const keys = Object.keys(row);
  const lower = new Map(keys.map((k) => [String(k).toLowerCase(), k]));
  for (let i = 0; i < names.length; i += 1) {
    const name = names[i];
    if (row[name] !== undefined && row[name] !== '') return name;
    const hit = lower.get(String(name).toLowerCase());
    if (hit !== undefined && row[hit] !== '') return hit;
  }
  return null;
}

function columnVariance(ds, key) {
  const n = datasetLength(ds);
  if (!n || !key) return 0;
  let count = 0;
  let mean = 0;
  let m2 = 0;
  const step = Math.max(1, Math.floor(n / 800));
  for (let i = 0; i < n; i += step) {
    const v = num(valueAt(ds, key, i));
    if (!Number.isFinite(v)) continue;
    count += 1;
    const d = v - mean;
    mean += d / count;
    m2 += d * (v - mean);
  }
  if (count < 8) return 0;
  return m2 / count;
}

function inferGradeKey(ds, preferred) {
  const n = datasetLength(ds);
  if (!n) return preferred || null;
  const preferredHit = preferred ? getCol(ds, [preferred]) : null;
  if (preferredHit && columnVariance(ds, preferredHit) > 1e-8) return preferredHit;
  const named = getCol(ds, GRADE_COLS);
  if (named) return named;
  const skip = new Set(SKIP_GRADE_NAMES.map((x) => String(x).toLowerCase()));
  const keys = ds.columns || (ds[0] ? Object.keys(ds[0]) : []);
  let best = null;
  let bestVar = 0;
  for (let k = 0; k < keys.length; k += 1) {
    if (skip.has(keys[k].toLowerCase())) continue;
    const v = columnVariance(ds, keys[k]);
    if (v > bestVar) {
      bestVar = v;
      best = keys[k];
    }
  }
  return best || preferredHit || preferred || null;
}

function pickCsvInZip(zip) {
  const names = Object.keys(zip.files).filter((key) => {
    const entry = zip.files[key];
    if (!entry || entry.dir) return false;
    const base = key.split('/').pop() || key;
    if (base.startsWith('.') || key.startsWith('__MACOSX')) return false;
    return true;
  });
  return names.find((k) => /\.csv$/i.test(k)) || names[0] || Object.keys(zip.files)[0];
}

function sizeLabel(size) {
  if (size == null || size === '') return '';
  if (typeof size === 'number') return formatBytes(size);
  return String(size);
}

function medianPositiveDelta(sorted) {
  const deltas = [];
  for (let i = 1; i < sorted.length; i += 1) {
    const d = sorted[i] - sorted[i - 1];
    if (d > 1e-4) deltas.push(d);
  }
  if (!deltas.length) return 2.5;
  deltas.sort((a, b) => a - b);
  return deltas[Math.floor(deltas.length / 2)];
}

function uniqueSorted(values) {
  const set = new Set();
  for (let i = 0; i < values.length; i += 1) {
    if (Number.isFinite(values[i])) set.add(Math.round(values[i] * COORD_ROUND) / COORD_ROUND);
  }
  return Array.from(set).sort((a, b) => a - b);
}

function typicalSize(ds, names) {
  const col = getCol(ds, names);
  if (!col) return NaN;
  const n = datasetLength(ds);
  const samples = [];
  const step = Math.max(1, Math.floor(n / 4000));
  for (let i = 0; i < n; i += step) {
    const v = num(valueAt(ds, col, i));
    if (Number.isFinite(v) && v > 1e-4) samples.push(v);
  }
  if (!samples.length) return NaN;
  samples.sort((a, b) => a - b);
  return samples[Math.floor(samples.length * 0.1)] || samples[0];
}

function gridKey(ix, iy, iz) {
  return `${ix}|${iy}|${iz}`;
}

function csvColumns(ds) {
  if (!ds) return [];
  const cols = ds.columns || (ds[0] ? Object.keys(ds[0]) : []);
  return cols.filter((c) => c != null && String(c).trim() !== '');
}

function columnNumericRange(ds, key) {
  const n = datasetLength(ds);
  if (!n || !key) return { isNumeric: false, min: null, max: null };
  let min = Infinity;
  let max = -Infinity;
  let numeric = 0;
  let total = 0;
  const step = Math.max(1, Math.floor(n / 20000));
  for (let i = 0; i < n; i += step) {
    const raw = valueAt(ds, key, i);
    if (raw == null || raw === '') continue;
    total += 1;
    const v = num(raw);
    if (!Number.isFinite(v)) continue;
    numeric += 1;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const isNumeric = total > 0 && numeric / total >= 0.7 && Number.isFinite(min) && Number.isFinite(max);
  return { isNumeric, min: isNumeric ? min : null, max: isNumeric ? max : null };
}

function axisLookup(ds, spec, fallbackNames) {
  if (typeof spec === 'number' && Number.isFinite(spec) && spec > 0) {
    return { col: null, fixed: spec };
  }
  const names = typeof spec === 'string' && spec.trim() ? [spec.trim()] : fallbackNames;
  return { col: getCol(ds, names), fixed: NaN };
}

function axisSpacing(ds, lookup, uniqueCoords, fallbackNames) {
  if (Number.isFinite(lookup.fixed) && lookup.fixed > 0) return lookup.fixed;
  const names = lookup.col ? [lookup.col] : fallbackNames;
  const fromCol = typicalSize(ds, names);
  if (Number.isFinite(fromCol) && fromCol > 0) return fromCol;
  const fromGrid = medianPositiveDelta(uniqueSorted(uniqueCoords));
  if (Number.isFinite(fromGrid) && fromGrid > 0) return fromGrid;
  return 2.5;
}

const COLLAPSE_LRU_MAX = 8;
const collapseLru = new Map();

function collapseCellsCached(ds, gradeKey, colorByKey, sizeSpec, cacheId) {
  const n = datasetLength(ds);
  const key = `${cacheId}|${n}|${gradeKey}|${colorByKey || ''}|${sizeSpec?.sx}|${sizeSpec?.sy}|${sizeSpec?.sz}`;
  if (collapseLru.has(key)) {
    const hit = collapseLru.get(key);
    collapseLru.delete(key);
    collapseLru.set(key, hit);
    return hit;
  }
  const packed = collapseCells(ds, gradeKey, colorByKey, sizeSpec);
  collapseLru.set(key, packed);
  while (collapseLru.size > COLLAPSE_LRU_MAX) {
    const oldest = collapseLru.keys().next().value;
    collapseLru.delete(oldest);
  }
  return packed;
}

function collapseCells(ds, gradeKey, colorByKey, sizeSpec = {}) {
  const n = datasetLength(ds);
  if (!n || !gradeKey) return { cells: [], spacing: { x: 2.5, y: 2.5, z: 2.5 } };

  const xCol = getCol(ds, ['XC', 'XCENTRE', 'EAST', 'Easting', 'X']);
  const yCol = getCol(ds, ['YC', 'YCENTRE', 'NORTH', 'Northing', 'Y']);
  const zCol = getCol(ds, ['ZC', 'ZCENTRE', 'RL', 'ELEV', 'Z']);
  if (!xCol || !yCol || !zCol) return { cells: [], spacing: { x: 2.5, y: 2.5, z: 2.5 } };

  const axisX = axisLookup(ds, sizeSpec.sx, SIZE_COLS.x);
  const axisY = axisLookup(ds, sizeSpec.sy, SIZE_COLS.y);
  const axisZ = axisLookup(ds, sizeSpec.sz, SIZE_COLS.z);
  const colorCol = colorByKey && colorByKey !== gradeKey ? getCol(ds, [colorByKey]) : null;

  const xs = new Float32Array(n);
  const ys = new Float32Array(n);
  const zs = new Float32Array(n);
  const sxs = new Float32Array(n);
  const sys = new Float32Array(n);
  const szs = new Float32Array(n);
  const grades = new Float32Array(n);
  const colorVals = colorCol ? new Array(n) : null;
  let kept = 0;

  for (let i = 0; i < n; i += 1) {
    const g = num(valueAt(ds, gradeKey, i));
    if (!Number.isFinite(g)) continue;
    const x = num(valueAt(ds, xCol, i));
    const y = num(valueAt(ds, yCol, i));
    const z = num(valueAt(ds, zCol, i));
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    xs[kept] = x;
    ys[kept] = y;
    zs[kept] = z;
    sxs[kept] = axisX.col ? num(valueAt(ds, axisX.col, i)) : axisX.fixed;
    sys[kept] = axisY.col ? num(valueAt(ds, axisY.col, i)) : axisY.fixed;
    szs[kept] = axisZ.col ? num(valueAt(ds, axisZ.col, i)) : axisZ.fixed;
    grades[kept] = g;
    if (colorVals) colorVals[kept] = valueAt(ds, colorCol, i);
    kept += 1;
  }
  if (!kept) return emptyPacked();

  const spacing = {
    x: axisSpacing(ds, axisX, xs.subarray(0, kept), SIZE_COLS.x),
    y: axisSpacing(ds, axisY, ys.subarray(0, kept), SIZE_COLS.y),
    z: axisSpacing(ds, axisZ, zs.subarray(0, kept), SIZE_COLS.z),
  };

  const best = new Map();
  for (let i = 0; i < kept; i += 1) {
    const ix = Math.round(xs[i] / spacing.x);
    const iy = Math.round(ys[i] / spacing.y);
    const iz = Math.round(zs[i] / spacing.z);
    const key = gridKey(ix, iy, iz);
    const prev = best.get(key);
    if (!prev || grades[i] > prev.g) {
      best.set(key, {
        x: xs[i],
        y: ys[i],
        z: zs[i],
        g: grades[i],
        cv: colorVals ? colorVals[i] : grades[i],
        sx: Number.isFinite(sxs[i]) && sxs[i] > 1e-4 ? sxs[i] : spacing.x,
        sy: Number.isFinite(sys[i]) && sys[i] > 1e-4 ? sys[i] : spacing.y,
        sz: Number.isFinite(szs[i]) && szs[i] > 1e-4 ? szs[i] : spacing.z,
      });
    }
  }

  return packCells(Array.from(best.values()), spacing);
}

function packCells(objects, spacing) {
  const count = objects.length;
  const x = new Float32Array(count);
  const y = new Float32Array(count);
  const z = new Float32Array(count);
  const g = new Float32Array(count);
  const sx = new Float32Array(count);
  const sy = new Float32Array(count);
  const sz = new Float32Array(count);
  const cv = new Array(count);
  for (let i = 0; i < count; i += 1) {
    const c = objects[i];
    x[i] = c.x;
    y[i] = c.y;
    z[i] = c.z;
    g[i] = c.g;
    sx[i] = c.sx;
    sy[i] = c.sy;
    sz[i] = c.sz;
    cv[i] = c.cv;
  }
  return { count, x, y, z, g, sx, sy, sz, cv, spacing };
}

function emptyPacked(spacing = { x: 2.5, y: 2.5, z: 2.5 }) {
  return {
    count: 0,
    x: new Float32Array(0),
    y: new Float32Array(0),
    z: new Float32Array(0),
    g: new Float32Array(0),
    sx: new Float32Array(0),
    sy: new Float32Array(0),
    sz: new Float32Array(0),
    cv: [],
    spacing,
  };
}

function gradeRange(packed) {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < (packed?.count || 0); i += 1) {
    const gv = packed.g[i];
    if (!Number.isFinite(gv)) continue;
    if (gv < min) min = gv;
    if (gv > max) max = gv;
  }
  if (!Number.isFinite(min)) return { min: null, max: null };
  return { min, max };
}

function filterPacked(packed, minGrade, maxGrade, dataMax, thresholds) {
  if (!packed?.count) return emptyPacked(packed?.spacing);
  let minG = Number.isFinite(minGrade) ? minGrade : DEFAULT_BLOCK_MODEL_MIN;
  let maxG = Number.isFinite(maxGrade) ? maxGrade : dataMax;
  if (maxG != null && minG != null && maxG < minG) {
    const swap = minG;
    minG = maxG;
    maxG = swap;
  }
  const keep = [];
  for (let i = 0; i < packed.count; i += 1) {
    const gv = packed.g[i];
    if (Number.isFinite(minG) && gv < minG) continue;
    if (Number.isFinite(maxG) && gv > maxG) continue;
    if (!isGradeBinVisible(gv, thresholds)) continue;
    keep.push(i);
  }
  const count = keep.length;
  const x = new Float32Array(count);
  const y = new Float32Array(count);
  const z = new Float32Array(count);
  const g = new Float32Array(count);
  const sx = new Float32Array(count);
  const sy = new Float32Array(count);
  const sz = new Float32Array(count);
  const cv = new Array(count);
  for (let j = 0; j < count; j += 1) {
    const i = keep[j];
    x[j] = packed.x[i];
    y[j] = packed.y[i];
    z[j] = packed.z[i];
    g[j] = packed.g[i];
    sx[j] = packed.sx[i];
    sy[j] = packed.sy[i];
    sz[j] = packed.sz[i];
    cv[j] = packed.cv[i];
  }
  return { count, x, y, z, g, sx, sy, sz, cv, spacing: packed.spacing };
}

function createBlockPointMaterial() {
  return new THREE.ShaderMaterial({
    uniforms: {
      uViewportY: { value: 800 },
      ...objectClipUniforms(),
    },
    clipping: true,
    vertexShader: `
      #include <clipping_planes_pars_vertex>
      attribute vec3 color;
      attribute float aSize;
      varying vec3 vColor;
      varying vec3 vMdvWorldPos;
      uniform float uViewportY;
      void main() {
        vColor = color;
        vMdvWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = max(1.5, aSize * projectionMatrix[1][1] * uViewportY / max(0.001, -mvPosition.z));
        gl_Position = projectionMatrix * mvPosition;
        #include <clipping_planes_vertex>
      }
    `,
    fragmentShader: `
      ${OBJECT_CLIP_SHADER_HEADER}
      #include <clipping_planes_pars_fragment>
      varying vec3 vColor;
      void main() {
        #include <clipping_planes_fragment>
        mdvApplyObjectClip(vMdvWorldPos);
        vec2 p = gl_PointCoord * 2.0 - 1.0;
        float d = max(abs(p.x), abs(p.y));
        float t = clamp(fwidth(d) * 1.75, 0.06, 0.12);
        gl_FragColor = vec4(d > 1.0 - t ? vec3(0.07) : vColor, 1.0);
      }
    `,
    transparent: false,
    opacity: 1,
    depthWrite: true,
    depthTest: true,
    toneMapped: false,
  });
}

export default function BlockModels({
  name,
  url,
  dx,
  dy,
  dz,
  size,
  grade,
  minGrade: layerMinGrade,
  maxGrade: layerMaxGrade,
  colorBy: layerColorBy,
  sx: layerSx,
  sy: layerSy,
  sz: layerSz,
  bmvisible,
  onLoadComplete,
  onLoadFailed,
}) {
  const pointsRef = useRef();
  const colorRef = useRef(new THREE.Color());
  const materialRef = useRef();
  const viewportHeight = useThree((s) => s.size.height);
  const [rawRows, setRawRows] = useState(() => dataCache[url] || null);
  const [sidecar, setSidecar] = useState({});
  const { startLoading, stopLoading, updateProgress } = useLoadingTracker();
  const objectName = name || `BlockModel_${url}`;
  const meta = useMemo(
    () =>
      mergeBlockModelMeta(
        {
          grade,
          dx,
          dy,
          dz,
          minGrade: layerMinGrade,
          maxGrade: layerMaxGrade,
          colorBy: layerColorBy,
          sx: layerSx,
          sy: layerSy,
          sz: layerSz,
        },
        sidecar
      ),
    [grade, dx, dy, dz, layerMinGrade, layerMaxGrade, layerColorBy, layerSx, layerSy, layerSz, sidecar]
  );
  const originDx = Number.isFinite(Number(meta.dx)) ? Number(meta.dx) : 0;
  const originDy = Number.isFinite(Number(meta.dy)) ? Number(meta.dy) : 0;
  const originDz = Number.isFinite(Number(meta.dz)) ? Number(meta.dz) : 0;
  const gradeFilter = useBlockModelFilterStore((s) => s.filter);
  const colorBy = gradeFilter?.colorBy || meta.colorBy || null;
  const minGrade = resolvedGradeMin(gradeFilter);
  const maxGrade = Number.isFinite(gradeFilter?.max) ? gradeFilter.max : null;
  const gradeThresholds = useGradePaletteStore((s) => s.thresholds);

  useEffect(() => {
    let cancelled = false;
    fetchBlockModelSidecar(url).then((next) => {
      if (!cancelled) setSidecar(next);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const fetchDataWithProgress = async (fileUrl) => {
    const buffer = await getModelArrayBuffer(fileUrl, {
      onProgress: (info) => updateProgress(objectName, info),
    });
    return new Blob([buffer]);
  };

  const resolvedGrade = useMemo(() => inferGradeKey(rawRows, meta.grade), [rawRows, meta.grade]);
  const colorByKey = (colorBy && getCol(rawRows, [colorBy])) || resolvedGrade;
  const colorStats = useMemo(
    () => (rawRows && colorByKey ? columnNumericRange(rawRows, colorByKey) : { isNumeric: false, min: null, max: null }),
    [rawRows, colorByKey]
  );

  useEffect(() => {
    if (!bmvisible) return undefined;
    let cancelled = false;

    const fetchData = async () => {
      if (rawRows || dataCache[url]) {
        const cached = rawRows || dataCache[url];
        const columnar = toColumnar(cached);
        if (columnar !== cached) {
          dataCache[url] = columnar;
          setRawRows(columnar);
        } else if (!rawRows) {
          setRawRows(columnar);
        }
        onLoadComplete?.(objectName);
        return;
      }

      const onDisk = await matchCachedModel(url, { ignoreSearch: true });
      if (!onDisk) {
        const userConfirmed = window.confirm(
          `Block model is not available on your cache , do you want to download it${sizeLabel(size) ? ` @ ${sizeLabel(size)}` : ''}?`
        );
        if (!userConfirmed) return;
      }

      startLoading(objectName);
      try {
        const blob = await fetchDataWithProgress(url);
        if (cancelled) return;
        updateProgress(objectName, { phase: 'parsing', percent: 100, indeterminate: false });
        const zip = await JSZip.loadAsync(blob);
        const fileName = pickCsvInZip(zip);
        const zipFile = fileName ? zip.file(fileName) : null;
        if (!zipFile) throw new Error('Zip does not contain a CSV file');
        const fileData = await zipFile.async('string');
        const parsedData = d3.csvParse(fileData);
        const columnar = toColumnar(parsedData);
        parsedData.length = 0;
        if (cancelled) return;
        dataCache[url] = columnar;
        setRawRows(columnar);
        stopLoading(objectName);
        onLoadComplete?.(objectName);
      } catch (error) {
        console.error('Error loading the zipped CSV file:', error);
        stopLoading(objectName);
        onLoadFailed?.(objectName);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, bmvisible, objectName]);

  const collapsed = useMemo(
    () => collapseCellsCached(rawRows, resolvedGrade, colorByKey, { sx: meta.sx, sy: meta.sy, sz: meta.sz }, url),
    [rawRows, resolvedGrade, colorByKey, meta.sx, meta.sy, meta.sz, url]
  );
  const dataRange = useMemo(() => gradeRange(collapsed), [collapsed]);
  const packed = useMemo(
    () => filterPacked(collapsed, minGrade, maxGrade, dataRange.max, gradeThresholds),
    [collapsed, minGrade, maxGrade, dataRange, gradeThresholds]
  );
  useEffect(() => {
    if (!bmvisible || !packed?.count) {
      useBlockModelDataStore.getState().clearPacked(objectName);
      return undefined;
    }
    useBlockModelDataStore.getState().setPacked(objectName, packed, {
      dx: originDx,
      dy: originDy,
      dz: originDz,
      gradeKey: resolvedGrade,
    });
    return () => useBlockModelDataStore.getState().clearPacked(objectName);
  }, [objectName, packed, bmvisible, originDx, originDy, originDz, resolvedGrade]);
  const count = packed.count;
  const pointMaterial = useMemo(() => {
    const mat = createBlockPointMaterial();
    materialRef.current = mat;
    return mat;
  }, []);

  useEffect(() => {
    const isGradeColor = !colorByKey || colorByKey === resolvedGrade;
    useClickStore.getState().setGradeLegend(objectName, isGradeColor ? resolvedGrade : '', !!bmvisible && isGradeColor);
    if (!isGradeColor && !colorStats.isNumeric && bmvisible) {
      const codes = [];
      const seen = new Set();
      for (let i = 0; i < packed.count; i += 1) {
        const key = String(packed.cv[i] ?? '').trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        codes.push(key);
      }
      useClickStore.getState().setLithLegend(objectName, codes, true);
    } else {
      useClickStore.getState().setLithLegend(objectName, [], false);
    }
    return () => {
      useClickStore.getState().setGradeLegend(objectName, resolvedGrade, false);
      useClickStore.getState().setLithLegend(objectName, [], false);
    };
  }, [objectName, resolvedGrade, colorByKey, colorStats.isNumeric, bmvisible, packed]);

  useEffect(() => {
    if (!objectName) return;
    useBlockModelFilterStore.getState().setLayerMeta(objectName, {
      columns: csvColumns(rawRows),
      gradeKey: resolvedGrade,
      dataMin: dataRange.min,
      dataMax: dataRange.max,
    });
  }, [objectName, rawRows, resolvedGrade, dataRange]);

  const pickInstance = (id) => {
    const i = Math.floor(Number(id));
    if (!Number.isInteger(i) || i < 0 || i >= packed.count) return null;
    return {
      Name: name,
      XC: packed.x[i],
      YC: packed.y[i],
      ZC: packed.z[i],
      SX: packed.sx[i],
      SY: packed.sy[i],
      SZ: packed.sz[i],
      [resolvedGrade || 'gradeValue']: packed.g[i],
      ...(colorByKey && colorByKey !== resolvedGrade ? { [colorByKey]: packed.cv[i] } : {}),
      grade: `${packed.g[i]} g/t`,
    };
  };

  const buffers = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const color = colorRef.current;
    const isGradeColor = !colorByKey || colorByKey === resolvedGrade;
    const [east, rl, north] = getOriginERL();
    for (let i = 0; i < count; i += 1) {
      positions[i * 3] = packed.x[i] - east + originDx;
      positions[i * 3 + 1] = packed.z[i] - rl + originDy;
      positions[i * 3 + 2] = north - packed.y[i] + originDz;
      const vol = Math.max(0.05, packed.sx[i] * packed.sy[i] * packed.sz[i]);
      sizes[i] = Math.cbrt(vol) * BLOCK_GAP;
      if (isGradeColor) {
        color.copy(getGradeColorFromThresholds(packed.g[i], gradeThresholds));
      } else if (colorStats.isNumeric) {
        color.copy(getNumericRampColor(packed.cv[i], colorStats.min, colorStats.max));
      } else {
        color.setHex(getLithColorHex(packed.cv[i]));
      }
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }
    let maxSize = 5;
    for (let i = 0; i < count; i += 1) {
      if (sizes[i] > maxSize) maxSize = sizes[i];
    }
    return { positions, colors, sizes, maxSize };
  }, [packed, count, originDx, originDy, originDz, colorByKey, resolvedGrade, colorStats, gradeThresholds]);

  const pointRaycast = useMemo(() => {
    const cell = Math.max(
      packed.spacing?.x || 0,
      packed.spacing?.y || 0,
      packed.spacing?.z || 0,
      buffers.maxSize || 0,
      5
    );
    const threshold = cell * 0.75;
    return function raycast(raycaster, intersects) {
      if (useClickStore.getState().clickType === 'pan') return;
      const pos = this.geometry?.attributes?.position;
      if (!pos || pos.count === 0) return;
      const sphere = this.geometry.boundingSphere;
      if (!sphere || sphere.radius === 0 || !Number.isFinite(sphere.radius)) {
        this.geometry.computeBoundingSphere();
      }
      if (!raycaster.params.Points) raycaster.params.Points = {};
      const prev = raycaster.params.Points.threshold;
      raycaster.params.Points.threshold = threshold;
      const before = intersects.length;
      THREE.Points.prototype.raycast.call(this, raycaster, intersects);
      raycaster.params.Points.threshold = prev;
      // Points hits use `index`; clickStore / highlight expect `instanceId`.
      for (let i = before; i < intersects.length; i += 1) {
        const hit = intersects[i];
        if (!Number.isInteger(hit.instanceId) || hit.instanceId < 0) {
          hit.instanceId = hit.index;
        }
      }
    };
  }, [packed.spacing, buffers.maxSize]);

  useLayoutEffect(() => {
    const pts = pointsRef.current;
    if (!pts || count === 0) return;
    if (pts.material?.uniforms?.uViewportY) {
      pts.material.uniforms.uViewportY.value = viewportHeight;
    }
    pts.frustumCulled = true;
    pts.geometry.computeBoundingSphere();
    pts.geometry.computeBoundingBox();
  }, [buffers, count, viewportHeight]);

  useEffect(() => {
    return () => {
      const pts = pointsRef.current;
      pts?.geometry?.dispose();
      materialRef.current?.dispose();
    };
  }, []);

  if (!bmvisible || count === 0) return null;

  return (
    <points
      ref={pointsRef}
      frustumCulled
      raycast={pointRaycast}
      name={name}
      userData={{ pickInstance, mdvLayerKey: name, mdvClipKind: 'solid' }}
    >
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[buffers.positions, 3]} />
        <bufferAttribute attach="attributes-color" args={[buffers.colors, 3]} />
        <bufferAttribute attach="attributes-aSize" args={[buffers.sizes, 1]} />
      </bufferGeometry>
      <primitive object={pointMaterial} attach="material" />
    </points>
  );
}
